import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import {
  dashboardDataQueryKey,
  dashboardLayoutsQueryKey,
  type DashboardLayoutsResponse,
  type DashboardWidgetCategory,
} from '@/components/reporting/dashboard-layout-content';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

import type { DashboardResponse } from './dashboard-content';
import { DashboardWidgetPreview } from './dashboard-widget-preview';

type DashboardWidgetKind = 'metric' | 'chart' | 'section';

interface AvailableWidget {
  key: string;
  kind: DashboardWidgetKind;
  category: DashboardWidgetCategory;
  nameKey: string;
}

interface DraftLayout {
  id?: string;
  name: string;
  widgets: string[];
}

const CATEGORY_ORDER: DashboardWidgetCategory[] = [
  'finance',
  'people',
  'attendance',
  'groups',
  'spending',
  'communication',
  'programs',
  'content',
];

const layoutsEqual = (left: DraftLayout[], right: DraftLayout[]): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

export const DashboardCustomizerDialog = ({
  open,
  onOpenChange,
  activeIndex,
  onActiveIndexChange,
  onWidgetsChange,
  dateFrom,
  dateTo,
  baseCurrency,
  locale,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onWidgetsChange: (widgets: string[]) => void;
  dateFrom?: string;
  dateTo?: string;
  baseCurrency: string;
  locale: string;
}) => {
  const { t } = useTranslation('reports');
  const queryClient = useQueryClient();
  const churchId = useChurchStore((state) => state.church?.id);
  const layoutsQueryKey = dashboardLayoutsQueryKey(churchId);
  const [draftLayouts, setDraftLayouts] = useState<DraftLayout[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState<DraftLayout[]>([]);
  const initializedRef = useRef(false);

  const layoutsQuery = useQuery({
    queryKey: layoutsQueryKey,
    queryFn: () => api.get<DashboardLayoutsResponse>('/dashboard/layouts'),
    enabled: Boolean(churchId),
  });

  const dashboardQuery = useQuery({
    queryKey: dashboardDataQueryKey(churchId, dateFrom, dateTo),
    queryFn: () =>
      api.get<DashboardResponse>('/dashboard', {
        query: {
          dateFrom: dateFrom ?? undefined,
          dateTo: dateTo ?? undefined,
        },
      }),
    enabled: open,
  });

  useEffect(() => {
    if (!layoutsQuery.data || initializedRef.current) return;
    initializedRef.current = true;
    const nextDraft = layoutsQuery.data.layouts.map((layout) => ({
      id: layout.id,
      name: layout.name,
      widgets: [...layout.widgets],
    }));
    setDraftLayouts(nextDraft);
    setSavedSnapshot(nextDraft);
    const activeIndexFromServer = layoutsQuery.data.layouts.findIndex(
      (layout) => layout.id === layoutsQuery.data.activeLayoutId,
    );
    if (activeIndexFromServer >= 0) {
      onActiveIndexChange(activeIndexFromServer);
    }
  }, [layoutsQuery.data, onActiveIndexChange]);

  const activeLayout = draftLayouts[activeIndex];
  const selectedWidgets = useMemo(
    () => new Set(activeLayout?.widgets ?? []),
    [activeLayout?.widgets],
  );

  useEffect(() => {
    onWidgetsChange(activeLayout?.widgets ?? []);
  }, [activeLayout?.widgets, onWidgetsChange]);

  const isDirty = !layoutsEqual(draftLayouts, savedSnapshot);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.patch<DashboardLayoutsResponse>('/dashboard/layouts', {
        activeLayoutIndex: activeIndex,
        layouts: draftLayouts,
      }),
    onSuccess: (data) => {
      const nextDraft = data.layouts.map((layout) => ({
        id: layout.id,
        name: layout.name,
        widgets: [...layout.widgets],
      }));
      setDraftLayouts(nextDraft);
      setSavedSnapshot(nextDraft);
      queryClient.setQueryData(layoutsQueryKey, data);
      const activeIndexFromServer = data.layouts.findIndex(
        (layout) => layout.id === data.activeLayoutId,
      );
      if (activeIndexFromServer >= 0) {
        onActiveIndexChange(activeIndexFromServer);
      }
      toast.success(t('home.layouts.saved'));
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('home.layouts.saveFailed'));
    },
  });

  const availableWidgets = layoutsQuery.data?.availableWidgets ?? [];
  const maxLayouts = layoutsQuery.data?.maxLayouts ?? 3;
  const maxWidgetsPerLayout = layoutsQuery.data?.maxWidgetsPerLayout ?? availableWidgets.length;
  const atWidgetLimit = selectedWidgets.size >= maxWidgetsPerLayout;

  const widgetsByCategory = useMemo(() => {
    const grouped = new Map<DashboardWidgetCategory, AvailableWidget[]>();
    for (const category of CATEGORY_ORDER) {
      grouped.set(category, []);
    }
    for (const widget of availableWidgets) {
      grouped.get(widget.category)?.push(widget);
    }
    return grouped;
  }, [availableWidgets]);

  const layoutOptions = draftLayouts.map((layout, index) => ({
    value: index,
    label: layout.name,
  }));

  const toggleWidget = (key: string, checked: boolean) => {
    if (checked && !selectedWidgets.has(key) && atWidgetLimit) {
      toast.error(t('home.layouts.widgetLimit', { max: maxWidgetsPerLayout }));
      return;
    }
    setDraftLayouts((current) =>
      current.map((layout, index) => {
        if (index !== activeIndex) return layout;
        const widgets = new Set(layout.widgets);
        if (checked) widgets.add(key);
        else widgets.delete(key);
        return { ...layout, widgets: [...widgets] };
      }),
    );
  };

  const updateLayoutName = (name: string) => {
    setDraftLayouts((current) =>
      current.map((layout, index) => (index === activeIndex ? { ...layout, name } : layout)),
    );
  };

  const addLayout = () => {
    if (draftLayouts.length >= maxLayouts) return;
    const nextIndex = draftLayouts.length + 1;
    const defaultWidgets =
      layoutsQuery.data?.defaultWidgetKeys ??
      availableWidgets.slice(0, Math.min(6, availableWidgets.length)).map((widget) => widget.key);
    setDraftLayouts((current) => [
      ...current,
      {
        name: t('home.layouts.defaultName', { slot: nextIndex }),
        widgets: defaultWidgets,
      },
    ]);
    onActiveIndexChange(draftLayouts.length);
  };

  const removeLayout = () => {
    if (draftLayouts.length <= 1) return;
    setDraftLayouts((current) => current.filter((_, index) => index !== activeIndex));
    onActiveIndexChange(Math.max(0, activeIndex - 1));
  };

  const renderWidgetTile = (widget: AvailableWidget, options?: { fullWidth?: boolean }) => {
    const selected = selectedWidgets.has(widget.key);
    const disabled = !selected && atWidgetLimit;
    const label = t(widget.nameKey);

    return (
      <li key={widget.key} className={cn('min-w-0', options?.fullWidth && 'sm:col-span-2')}>
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-disabled={disabled}
          aria-label={t('home.layouts.selectTile', { name: label })}
          disabled={disabled}
          onClick={() => toggleWidget(widget.key, !selected)}
          className={cn(
            'relative flex h-full w-full flex-col rounded-xl p-1 text-left transition-shadow',
            disabled && 'cursor-not-allowed opacity-50',
            selected
              ? 'ring-2 ring-brand ring-offset-2 ring-offset-surface'
              : 'ring-1 ring-border hover:ring-border-strong',
          )}
        >
          <span
            className={cn(
              'absolute right-3 top-3 z-10 flex size-6 items-center justify-center rounded-full border bg-surface shadow-sm',
              selected
                ? 'border-brand bg-brand text-on-brand'
                : 'border-border-strong text-transparent',
            )}
            aria-hidden
          >
            <Check className="size-3.5" />
          </span>
          <div
            className={cn(
              'pointer-events-none flex-1 overflow-hidden',
              widget.kind === 'metric' && 'min-h-[7.5rem]',
              widget.kind === 'chart' && 'h-[11.5rem]',
              widget.kind === 'section' && 'h-[10rem]',
            )}
          >
            <DashboardWidgetPreview
              widgetKey={widget.key}
              dashboard={dashboardQuery.data}
              loading={previewLoading}
              baseCurrency={baseCurrency}
              locale={locale}
              compact
            />
          </div>
        </button>
      </li>
    );
  };

  const renderCategoryWidgets = (widgets: AvailableWidget[]) => {
    const metrics = widgets.filter((widget) => widget.kind === 'metric');
    const charts = widgets.filter((widget) => widget.kind === 'chart');
    const sections = widgets.filter((widget) => widget.kind === 'section');
    const oddChartCount = charts.length % 2 === 1;

    return (
      <div className="space-y-4">
        {metrics.length > 0 ? (
          <ul className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {metrics.map((widget) => renderWidgetTile(widget))}
          </ul>
        ) : null}
        {charts.length > 0 ? (
          <ul className="grid gap-4 sm:grid-cols-2">
            {charts.map((widget, index) =>
              renderWidgetTile(widget, {
                fullWidth: oddChartCount && index === charts.length - 1,
              }),
            )}
          </ul>
        ) : null}
        {sections.length > 0 ? (
          <ul className="grid gap-4">
            {sections.map((widget) => renderWidgetTile(widget))}
          </ul>
        ) : null}
      </div>
    );
  };

  const previewLoading = dashboardQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="2xl" className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('home.layouts.title')}</DialogTitle>
          <DialogDescription>{t('home.layouts.description')}</DialogDescription>
        </DialogHeader>

        {layoutsQuery.isLoading ? (
          <DialogBody>
            <p className="text-body-sm text-fg-muted">{t('home.layouts.loading')}</p>
          </DialogBody>
        ) : layoutsQuery.isError ? (
          <DialogBody contentClassName="space-y-3">
            <p className="text-body-sm text-fg-muted">{t('home.layouts.loadFailed')}</p>
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={() => layoutsQuery.refetch()}>
                {t('home.layouts.retry')}
              </Button>
            </div>
          </DialogBody>
        ) : (
          <>
            <DialogBody className="overflow-visible" contentClassName="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label={t('home.layouts.switchLabel')}>
                  {(field) => (
                    <Listbox
                      id={field.id}
                      value={activeIndex}
                      onChange={(value) => {
                        if (value === null) return;
                        onActiveIndexChange(value);
                      }}
                      options={layoutOptions}
                      placeholder={t('home.layouts.switchPlaceholder')}
                      popoverPortal="dialog"
                    />
                  )}
                </FormField>

                <FormField label={t('home.layouts.nameLabel')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      value={activeLayout?.name ?? ''}
                      onChange={(event) => updateLayoutName(event.target.value)}
                      maxLength={100}
                    />
                  )}
                </FormField>
              </div>

              <div className="space-y-6">
                <p className="text-body-sm text-fg-secondary">{t('home.layouts.previewHint')}</p>
                {availableWidgets.length === 0 ? (
                  <p className="text-body-sm text-fg-muted">{t('home.layouts.noWidgets')}</p>
                ) : (
                  CATEGORY_ORDER.map((category) => {
                    const widgets = widgetsByCategory.get(category) ?? [];
                    if (widgets.length === 0) return null;
                    return (
                      <div key={category} className="space-y-3">
                        <p className="text-caption font-medium uppercase tracking-wide text-fg-tertiary">
                          {t(`home.layouts.categories.${category}`)}
                        </p>
                        {renderCategoryWidgets(widgets)}
                      </div>
                    );
                  })
                )}
              </div>
            </DialogBody>

            <DialogFooter className="flex flex-wrap justify-end gap-2">
              {draftLayouts.length < maxLayouts ? (
                <Button type="button" variant="secondary" onClick={addLayout}>
                  <Plus className="size-4" aria-hidden />
                  {t('home.layouts.add')}
                </Button>
              ) : null}
              {draftLayouts.length > 1 ? (
                <Button type="button" variant="secondary" onClick={removeLayout}>
                  <Trash2 className="size-4" aria-hidden />
                  {t('home.layouts.remove')}
                </Button>
              ) : null}
              <Button
                type="button"
                disabled={!isDirty || saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? t('home.layouts.saving') : t('home.layouts.save')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
